import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ProductCategory, ProductCategoryValuesModel } from '@warema/shared/company-search/models';
import { WorkflowOrigin, WorkflowStepType } from '@warema/shared/models';
import { ProductCategoriesValues } from '@warema/shared/product-category';
import { WorkflowValidationApiService } from '@warema/shared/workflows/workflow-common/api';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, first, map, switchMap } from 'rxjs/operators';
import { SearchDataService } from '../../search/services/search-data.service';
import * as ProductCategoryActions from '../state/product-category.actions';
import { ProductCategoryState } from '../state/product-category.reducers';
import * as ProductCategorySelectors from '../state/product-category.selectors';

@Injectable({
    providedIn: 'root',
})
export class ProductCategoryDataService {
    private readonly _store = inject<Store<ProductCategoryState>>(Store);
    private readonly _route = inject(ActivatedRoute);
    private readonly _router = inject(Router);
    private readonly _searchDataService = inject(SearchDataService);
    private readonly _workflowValidationApiService = inject(WorkflowValidationApiService);

    private _isServiceInitialized = false;

    public productCategories$: Observable<Array<ProductCategory>>;
    public selectedSubProductCategoriesIds$: Observable<Array<number>>;
    public selectedSubProductCategoriesNames$: Observable<Array<string>>;
    public isPatioSelected$: Observable<boolean>;
    public isWindowSelected$: Observable<boolean>;
    public onlyControlSystemsSelected$: Observable<boolean>;
    public selectedSubProductCategoriesTranslationKeys$: Observable<Array<string>>;
    public productCategoriesValues: Array<ProductCategoryValuesModel> = ProductCategoriesValues;

    constructor() {
        this.productCategories$ = this._store.select(ProductCategorySelectors.selectAllProductCategories);
        this.selectedSubProductCategoriesIds$ = this._store.select(
            ProductCategorySelectors.selectAllSelectedProductCategoriesIds,
        );
        this.selectedSubProductCategoriesNames$ = this._store.select(
            ProductCategorySelectors.selectSelectedProductCategoriesNames,
        );
        this.isPatioSelected$ = this._store.select(ProductCategorySelectors.selectIsPatioSelected);
        this.isWindowSelected$ = this._store.select(ProductCategorySelectors.selectIsWindowSelected);
        this.onlyControlSystemsSelected$ = this._store.select(
            ProductCategorySelectors.selectOnlyControlSystemsSelected,
        );
        this.selectedSubProductCategoriesTranslationKeys$ = this._store.select(
            ProductCategorySelectors.selectAllSelectedProductCategoriesTranslationKeys,
        );
    }

    public validateProductCategories(): void {
        const isFormValid$ = this.selectedSubProductCategoriesIds$.pipe(map(x => x.length !== 0));

        this._workflowValidationApiService.manageWorkflowValidation(isFormValid$, WorkflowStepType.ProductCategories);
    }

    public initService(): void {
        if (this._isServiceInitialized) {
            return;
        }
        this._loadProductCategories();
        this._initProductCategoriesFromQueryParams();
        this._isServiceInitialized = true;
    }

    public async handleProductCategoryToggle(
        selectedSubProductCategoriesIds: Array<number>,
        subProductCategoryId: number,
    ): Promise<void> {
        const originQueryParam = this._route.snapshot.queryParams['origin'] as WorkflowOrigin;
        if (!originQueryParam || (originQueryParam && originQueryParam !== WorkflowOrigin.BusinessCard)) {
            this._searchDataService.clearCompanySearchDataExceptLocationAndInstantOffer();
        }

        const selectedSubProductCategoriesIdsAsString = this._toggleSubProductCategoryId(
            selectedSubProductCategoriesIds,
            subProductCategoryId,
        );

        await this._router.navigate([], {
            relativeTo: this._route,
            queryParams: { productSubcategories: selectedSubProductCategoriesIdsAsString },
            queryParamsHandling: 'merge',
        });
    }

    public activateOnlyOneSubCategory(subProductCategoryId: number): void {
        this._store.dispatch(ProductCategoryActions.ActivateOnlyOneSubCategory({ subProductCategoryId }));
    }

    private _loadProductCategories(): void {
        this.productCategories$
            .pipe(
                filter(x => x.length === 0),
                first(),
            )
            .subscribe(() => {
                this._store.dispatch(ProductCategoryActions.LoadProductCategories());
            });
    }

    private _initProductCategoriesFromQueryParams(): void {
        this.productCategories$
            .pipe(
                filter(categories => categories.length > 0),
                first(),
                switchMap(() => this._route.queryParams.pipe(distinctUntilChanged())),
            )
            .subscribe(params => {
                const productSubCategoriesIds = params['productSubcategories']
                    ? (params['productSubcategories'] as string).split(',').map(Number)
                    : [];
                this.selectProductCategories(productSubCategoriesIds);
            });
    }

    public selectProductCategories(ids: Array<number>): void {
        this._store.dispatch(ProductCategoryActions.SubProductCategoriesSelected({ ids }));
    }

    private _toggleSubProductCategoryId(
        selectedSubProductCategoriesIds: Array<number>,
        subProductCategoryId: number,
    ): string {
        const alreadySelectedIds = [...selectedSubProductCategoriesIds];
        const index = alreadySelectedIds.indexOf(subProductCategoryId);

        if (index !== -1) {
            alreadySelectedIds.splice(index, 1);
        } else {
            alreadySelectedIds.push(subProductCategoryId);
        }

        return alreadySelectedIds.join(',');
    }
}
